<script lang="ts" setup>
    import { useMouseInElement, useFetch, onClickOutside } from "@vueuse/core";
    import { ref, computed, watch, nextTick } from "vue";
    import {
        isPreviewing as builderIsPreviewing,
        isEditing
    } from "@builder.io/sdk-vue";
    import Cms from "@/components/Cms.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import type { Cta } from "@/helpers/builder-init/index";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    import { useColors } from "@/helpers/composables/useColors";
    import { useAnchorListener } from "@/helpers/composables/useAnchorListener";
    import CarretsWhite from "@icons/carrets-white.svg";
    import CarretsBlack from "@icons/carrets-black.svg";
    import WhiteX from "@icons/white-x.svg";
    import { v4 as uuidv4 } from "uuid";
    import useImageOptimization from "@composables/useImageOptimization";

    interface Point {
        id: string;
        x: number;
        y: number;
        details: {
            title: string;
            description: string;
        };
        display: boolean;
    }
    interface Badge {
        badge: string;
    }
    interface TextProp {
        textPosition: string;
        textAlignment: string;
        collabBadges?: Badge[];
        texturedBackground?: string;
        bgColor: string;
        textColor: string;
        eyeBrowColor: string;
        titleColor: string;
        eyebrow: string;
        title: string;
        descriptionColor: string;
        description: string;
        descriptionPosition: string;
        cta: Cta;
    }

    type ContentPosition = "image-left" | "image-right";
    let props = defineProps({
        interactive: {
            type: Boolean,
            required: false,
            default: true
        },
        elementId: {
            type: String,
            required: false
        },
        bgImage: {
            type: String,
            required: false
        },
        image: {
            type: String,
            required: true
        },
        imageAlt: {
            type: String,
            required: false
        },
        contentPosition: {
            type: String as PropType<ContentPosition>,
            required: true,
            default: "image-right"
        },
        textProps: {
            type: Object as PropType<TextProp>,
            required: true,
            default() {
                return {
                    bgColor: "rothcoCharcoal",
                    eyebrow: "BUILT FOR THE CHALLENGE",
                    title: "Purpose-Driven Gear for Every Adventure",
                    description:
                        "The convergence of Rothco and Fred Bear Camo brings together an expert in development of not only camo patterns, but styles made to last.",
                    cta: {
                        type: "button",
                        classes: "secondary",
                        text: "SHOP ROTHCO x BEAR ARCHERY",
                        link: "#"
                    }
                };
            }
        }
    });
    let imageRef = ref();
    let imagePoints: Ref<Point[]> = ref([]);

    let {
        elementPositionX,
        elementPositionY,
        elementX,
        elementY,
        isOutside,
        elementHeight,
        elementWidth
    } = useMouseInElement(imageRef);

    const handleHighlightClick = (event) => {
        let x = Math.floor((elementX.value / elementWidth.value) * 100);
        let y = Math.floor((elementY.value / elementHeight.value) * 100);
        if (isOutside.value) {
            return;
        }
        let existingPoint = imagePoints.value.find((p) => {
            return p.x >= x - 5 && p.x <= x + 5 && p.y >= y - 5 && p.y <= y + 5;
        });

        if (existingPoint) {
            showPointDetails(existingPoint);
            return;
        }

        //check if click is in any tooltip
        let tooltipClicked = false;
        for (let i = 0; i < imagePoints.value.length; i++) {
            let point = imagePoints.value[i];
            let tooltip = document.getElementById(point.id + "-point-tooltip");
            let rect = tooltip?.getBoundingClientRect?.() || {
                left: 0,
                right: 0,
                top: 0,
                bottom: 0
            };
            if (
                elementX.value >= rect.left &&
                elementX.value <= rect.right &&
                elementY.value >= rect.top &&
                elementY.value <= rect.bottom
            ) {
                tooltipClicked = true;
                break;
            }
        }
        if (tooltipClicked) {
            return;
        }
        if (!isPreviewing()) {
            return;
        }
        imagePoints.value.push({
            id: uuidv4(),
            x: x,
            y: y,
            details: {
                title: "Title",
                description: ""
            },
            display: false
        });
    };

    let imagePositions = computed(() => {
        // get position of image from ref
        return {
            x: elementPositionX.value,
            y: elementPositionY.value
        };
    });

    let searchParams = computed(() => ({
        "data.elementId.$eq": props.elementId
    }));
    let showPointDetails = (point, mobileTouch = true) => {
        if (imagePoints.value.some((p) => p.display)) {
            imagePoints.value.forEach((p) => (p.display = false));
        }
        point.display = true;

        let tooltip = document.getElementById(point.id + "-point-tooltip");
        if (tooltip) {
            nextTick(() => {
                tooltip.querySelector("input")?.focus();
            });
        }


        if(mobileTouch){
            mobileDetailIndex.value = imagePoints.value.findIndex((p) => p.id === point.id);
        }
    };

    let savePointsPayload = computed(() => ({
        published: "published",
        name: props.elementId,
        data: {
            elementId: props.elementId,
            points: imagePoints.value.map((p) => {
                return {
                    id: p.id,
                    x: p.x,
                    y: p.y,
                    details: p.details
                };
            })
        }
    }));

    let isPreviewing = () => {
        return (props.interactive && builderIsPreviewing()) || isEditing();
    };
    let success = ref(false);
    function showSuccess() {
        success.value = true;
        setTimeout(() => {
            success.value = false;
        }, 5000);
    }
    function savePoints() {
        if (builderId.value == null) {
            let { data } = useFetch(
                "https://builder.io/api/v1/write/highlight-image-points",
                {
                    beforeFetch(ctx) {
                        let options = ctx.options;
                        options.headers = {
                            Authorization: `Bearer ${import.meta.env.VITE_BUILDER_IO_WRITE_API_KEY}`,
                            "Content-Type": "application/json",
                            "Accept": "application/json"
                        };

                        ctx.options = options;
                        return ctx;
                    },
                    afterFetch(ctx){
                        if(ctx.response.ok){
                            showSuccess();
                        }
                        return ctx;
                    }
                }
            )
                .post(savePointsPayload.value)
                .json();
        } else {
            let { data } = useFetch(
                `https://builder.io/api/v1/write/highlight-image-points/${builderId.value}`,
                {
                    beforeFetch(ctx) {
                        let options = ctx.options;
                        options.headers = {
                            Authorization: `Bearer ${import.meta.env.VITE_BUILDER_IO_WRITE_API_KEY}`,
                            "Content-Type": "application/json",
                            "Accept": "application/json"
                        };

                        ctx.options = options;
                        return ctx;
                    },
                    afterFetch(ctx){
                        if(ctx.response.ok){
                            showSuccess();
                        }
                        return ctx;
                    }
                }
            )
                .patch(savePointsPayload.value)
                .json();
        }
    }

    let builderData = ref([]);
    let builderId = computed(() => {
        if (builderData.value.length > 0) {
            return builderData.value[0].builderId;
        }
        return null;
    });
    watch(
        () => builderData.value.length,
        (newVal) => {
            if (newVal > 0) {
                imagePoints.value = builderData.value[0].points.map((p) => {
                    return {
                        id: p.id,
                        x: p.x,
                        y: p.y,
                        details: JSON.parse(JSON.stringify(p.details)),
                        display: false
                    };
                });
            }
        },
        { deep: true }
    );

    let { getColorValue } = useColors();

    let editPointDetails = (point) => {
        if (!point.display) {
            point.display = true;
        }
    };

    let deletePoint = (point) => {
        let index = imagePoints.value.findIndex((p) => p.id === point.id);
        imagePoints.value.splice(index, 1);
    };

    let mobileDetailIndex = ref(0);
    let mobileDetail = computed(() => {
        return imagePoints.value[mobileDetailIndex.value]?.details?.description;
    });

    let nextMobileDetail = () => {
        mobileDetailIndex.value = mobileDetailIndex.value + 1;
        if (mobileDetailIndex.value >= imagePoints.value.length) {
            mobileDetailIndex.value = 0;
        }
        showPointDetails(imagePoints.value[mobileDetailIndex.value], false);
        
    };

    let prevMobileDetail = () => {
        mobileDetailIndex.value = mobileDetailIndex.value - 1;
        if (mobileDetailIndex.value < 0) {
            mobileDetailIndex.value = imagePoints.value.length - 1;
        }
        showPointDetails(imagePoints.value[mobileDetailIndex.value], false);
        
    };

    let hasTextedBackground = computed(() => {
        return props.textProps.texturedBackground?.length > 0;
    });

    let hasCollabBadges = computed(() => {
        return props.textProps.collabBadges?.length > 0;
    });

    let textAlignmentClasses = computed(() => {
        let classes = {
            "flex-column": true
        };

        switch (props.textProps.textPosition) {
            case "center":
                classes["items-center"] = true;
                break;
            case "left":
                classes["items-start"] = true;
                break;
            case "right":
                classes["items-end"] = true;
                break;
            default:
                break;
        }

        switch (props.textProps.textAlignment) {
            case "center":
                classes["text-center"] = true;
                break;
            case "left":
                classes["text-left"] = true;
                break;
            case "right":
                classes["text-right"] = true;
                break;
            default:
                break;
        }

        return classes;
    });

    let textDescription = computed(() => {
        if (typeof window === "undefined") {
            return props.textProps.description;
        }
        let parser = new DOMParser();
        let parsed = parser.parseFromString(
            props.textProps.description,
            "text/html"
        );
        return parsed.body.firstChild?.innerHTML ?? "";
    });

    const descriptionContainer = ref<HTMLElement | null>(null);

    useAnchorListener(descriptionContainer);

    const getCtaClasses = (cta) => {
        let classes = [cta.style ? cta.style : "primary"];
        if (cta.size != "default") classes.push(cta.size);
        return classes;
    };

    function getBadge(index) {
        return props.textProps.collabBadges[index - 1]?.badge;
    }

    let activePoint = ref();

    onClickOutside(activePoint, () => {
        if (activePoint.value) {
            // find active point in imagePoints and set display to false

            let point = imagePoints.value.findIndex((p) => p.display);
            if (point > -1) {
                imagePoints.value[point].display = false;
            }
        }
    });

    let { optimize } = useImageOptimization();
</script>
<template>
    <div>
        <Cms
            v-show="false"
            v-model="builderData"
            is-data
            driver="builder"
            model="highlight-image-points"
            :searchParams="searchParams"
        />
        <div v-if="isPreviewing() && interactive" style="margin: 1rem;" :class="{
            'flex-row': true,
            'justify-between': success,
            'justify-end': !success,
            'align-center': true
        }">
            <div v-show="success" class="notification notification-success" style="width: 50%">
                <p class="paragraph-details"><strong>Success!</strong> Your points saved successfully!</p>
            </div>

            <button class="primary" @click="savePoints">
                SAVE POINTS
                <div aria-label="hidden" class="icon">
                    <CarretsWhite alt="" />
                </div>
            </button>
        </div>
        <div
            v-if="contentPosition == 'image-right'"
            class="fw_banner interactive"
        >
            <div
                :class="{
                    'text-section': true,
                    [`bg-` + textProps.bgColor]: true
                }"
            >
                <div class="text-section-container">
                    <div
                        :class="{
                            wrapper: true,
                            ...textAlignmentClasses
                        }"
                    >
                        <img
                            v-if="hasTextedBackground"
                             
                            :src="textProps.texturedBackground"
                            alt="Textured background"
                            class="wrapper-image"
                        />
                        <div
                            :class="{
                                'wrapper-content': true
                            }"
                        >
                            <div class="icons-container" v-if="hasCollabBadges">
                                <img

                                    :src="getBadge(1)"
                                    alt="Collab Badge"
                                    class="icon-1"
                                />
                               <WhiteX alt="Brand logo 2" class="icon-2" />
                               <img
                                :src="getBadge(2)"
                                alt="Collab Badge"
                                class="icon-1"
                                />
                            </div>
                            <p
                                :class="{
                                    eyebrow: true,
                                    [`text-${textProps.eyeBrowColor}`]: true
                                }"
                            >
                                {{ textProps.eyebrow }}
                            </p>
                            <h2
                                :class="{
                                    title: true,
                                    'headline-xl-light': true,
                                    [`text-${textProps.titleColor}`]: true
                                }"
                            >
                                {{ textProps.title }}
                            </h2>
                            <p
                                id="description-container"
                                ref="descriptionContainer"
                                v-if="textProps.descriptionPosition == 'above cta' || !textProps.descriptionPosition"
                                :class="{
                                    paragraph: true,
                                    [`text-${textProps.textColor}`]: true
                                }"
                                v-html="textDescription"
                            ></p>
                            <div class="button-container" :style="`align-self: ${props.textProps.textAlignment == 'right' ? 'end' : props.textProps.textAlignment};`">
                                <RothcoCta
                                    v-if="textProps?.cta"
                                    :ctaType="textProps.cta.type"
                                    :class="[...getCtaClasses(textProps.cta)]"
                                    v-bind="getCtaLink(textProps.cta)"
                                    :aria-label="textProps.cta.aria_label"
                                >
                                    {{ textProps.cta.label }}
                                </RothcoCta>
                            </div>
                            <p
                                id="description-container"
                                ref="descriptionContainer"
                                v-if="textProps.descriptionPosition == 'below cta'"
                                :class="{
                                    paragraph: true,
                                    [`text-${textProps.textColor}`]: true,
                                    'subtext': true
                                }"
                                v-html="textDescription"
                            ></p>
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="image-section"
                :style="{
                    cursor: isPreviewing() ? 'pointer' : 'default'
                }"
                @click="handleHighlightClick"
                ref="imageRef"
            >
                <!-- Image -->
                <img
                     
                    v-if="bgImage"
                    :src="optimize(bgImage)"
                    class="background"
                />
                <img
                    :src="optimize(image)"
                    :alt="imageAlt"
                    class="page_container-backpack"
                />
                <div
                    @click="showPointDetails(point)"
                    v-for="point in imagePoints"
                    :key="point.description"
                    :class="{
                        'interactive-image_bullet': true,
                        active: point.display
                    }"
                    :ref="point.display && !isEditing() || !isPreviewing() ? 'activePoint' : ''"
                    :style="{ left: point.x + '%', top: point.y + '%' }"
                >
                    <div
                        @click.stop.prevent="editPointDetails(point)"
                        :id="point.id + '-point-tooltip'"
                        class="top left"
                    >
                        <PrimeInplace
                            :active="isPreviewing()"
                            :disabled="!isPreviewing()"
                        >
                            <template #display>
                                {{ point.details.description }}
                            </template>
                            <template #content="{ closeCallBack }">
                                <div class="flex" @click.stop.prevent="()=>{}">
                                    <input
                                        placeholder="Enter description"
                                        @click.stop.prevent="()=>{}"
                                        :class="`text-${textProps.textColor}`"
                                        v-model.lazy="point.details.description"
                                        @blur="closeCallBack"
                                        autofocus
                                    />
                                    <button
                                        @click="deletePoint(point)"
                                        class="primary"
                                    >
                                        Delete Point
                                    </button>
                                </div>
                            </template>
                        </PrimeInplace>
                    </div>
                </div>
            </div>
            <!-- Mobile Interactive Text and Carousel-->
            <div v-if="interactive" class="interactive-controls">
                    <div class="interactive-controls_container">
                        <button
                            class="interactive-controls_prev"
                            @click="prevMobileDetail"
                        >
                            <CarretsBlack
                                alt="Previous"
                            />
                        </button>
                        <div class="interactive-controls_text">
                            <p>{{ mobileDetail }}</p>
                        </div>
                        <button
                            class="interactive-controls_next"
                            @click="nextMobileDetail"
                        >
                            <CarretsBlack src="@icons/carrets-black.svg" alt="Next" />
                        </button>
                    </div>
            </div>
        </div>
        <div v-else class="fw_banner interactive">
            <div
                class="image-section"
                :style="{
                    cursor: isPreviewing() ? 'pointer' : 'default'
                }"
                @click="handleHighlightClick"
                ref="imageRef"
            >
                <!-- Image -->
                <img
                    loading="lazy"
                    v-if="bgImage"
                    :src="optimize(bgImage)"
                    class="background"
                />
                <img
                    :src="optimize(image)"
                    :alt="imageAlt"
                    class="page_container-backpack"
                />

                <div
                    @click="showPointDetails(point)"
                    v-for="point in imagePoints"
                    :key="point.description"
                    :class="{
                        'interactive-image_bullet': true,
                        active: point.display
                    }"
                    :style="{ left: point.x + '%', top: point.y + '%' }"
                >
                    <div
                        @click.stop.prevent="editPointDetails(point)"
                        :id="point.id + '-point-tooltip'"
                        class="top right"
                        
                    >
                        <PrimeInplace
                            :active="isPreviewing()"
                            :disabled="!isPreviewing()"
                        >
                            <template #display>
                                {{ point.details.description }}
                            </template>
                            <template #content="{ closeCallBack }">
                                <div class="flex" @click.stop.prevent="()=>{}">
                                    <input
                                        placeholder="Enter description"
                                        @click.stop.prevent="()=>{}"
                                        :class="`text-${textProps.textColor}`"
                                        v-model.lazy="point.details.description"
                                        @blur="closeCallBack"
                                        autofocus
                                    />
                                    <button
                                        @click="deletePoint(point)"
                                        class="primary"
                                    >
                                        Delete Point
                                    </button>
                                </div>
                            </template>
                        </PrimeInplace>
                    </div>
                </div>
            </div>
            <!-- Mobile Interactive Text and Carousel-->
            <div v-if="interactive" class="interactive-controls">
                <div class="interactive-controls_container">
                    <button
                        class="interactive-controls_prev"
                        @click="prevMobileDetail"
                    >
                        <CarretsBlack
                                alt="Previous"
                        />
                    </button>
                    <div class="interactive-controls_text">
                        <p>{{ mobileDetail }}</p>
                    </div>
                    <button
                        class="interactive-controls_next"
                        @click="nextMobileDetail"
                    >
                        <CarretsBlack alt="Next" />
                    </button>
                </div>
            </div>
            <div
                :class="{
                    'text-section': true,
                    [`bg-` + textProps.bgColor]: true
                }"
            >
                <div class="text-section-container">
                    <div
                        :class="{
                            wrapper: true,
                            ...textAlignmentClasses
                        }"
                    >
                        <img
                            v-if="hasTextedBackground"
                            loading="lazy"
                            :src="textProps.texturedBackground"
                            alt="Textured background"
                            class="wrapper-image"
                        />
                        <div class="wrapper-content">
                            <p
                                :class="{
                                    eyebrow: true,
                                    [`text-${textProps.eyeBrowColor}`]: true
                                }"
                            >
                                {{ textProps.eyebrow }}
                            </p>
                            <h2
                                :class="{
                                    title: true,
                                    'headline-xl-light': true,
                                    [`text-${textProps.titleColor}`]: true
                                }"
                            >
                                {{ textProps.title }}
                            </h2>
                            <p
                                :class="{
                                    paragraph: true,
                                    [`text-${textProps.textColor}`]: true
                                }"
                                v-html="textDescription"
                            ></p>
                            <div class="button-container" :style="`align-self: ${props.textProps.textAlignment == 'right' ? 'end' : props.textProps.textAlignment};`">
                                <RothcoCta
                                    v-if="textProps?.cta"
                                    :ctaType="textProps.cta.type"
                                    :class="[...getCtaClasses(textProps.cta)]"
                                    v-bind="getCtaLink(textProps.cta)"
                                    :aria-label="textProps.cta.aria_label"
                                >
                                    {{ textProps.cta.label }}
                                </RothcoCta>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
